<template>
  <div class="mb-print">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Monthly Breakup Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ subjectBlocks.length }} Subjects
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading monthly breakup...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadData">Retry</b-button>
    </div>

    <div v-else-if="!subjectBlocks.length" class="text-center py-5">
      <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
      <h5>No monthly breakup found for selected class/section.</h5>
    </div>

    <div
      v-for="(subject, sIndex) in subjectBlocks"
      :key="'subject-' + (subject.subjectID || sIndex)"
      class="report-sheet"
      :class="{ 'page-break': sIndex > 0 }"
    >
      <div class="letterhead">
        <img
          v-if="logo"
          :src="logo"
          alt="School logo"
          class="school-logo"
        />
        <div v-else class="school-logo school-logo--fallback">
          {{ campusInitials }}
        </div>
        <p v-if="showCampusNameText" class="school-name">
          {{ campusName }}
        </p>
        <p v-if="campusTagline" class="school-tagline">{{ campusTagline }}</p>

        <h2 class="report-title">{{ reportTitle(subject) }}</h2>
        <p class="session-line">SESSION {{ sessionName || "—" }}</p>
      </div>

      <table class="meta-table">
        <tr>
          <th>Teacher's Name:</th>
          <td>{{ subject.teacherName || teacherName || "—" }}</td>
        </tr>
        <tr>
          <th>Class / Stage:</th>
          <td>{{ subjectClassLabel(subject) }}</td>
        </tr>
      </table>

      <table class="breakup-table">
        <thead>
          <tr>
            <th colspan="3" class="breakup-head">
              {{ subject.subjectName || subjectName || "MONTHLY BREAKUP" }}
            </th>
          </tr>
          <tr class="col-head">
            <th class="col-sno">S.NO</th>
            <th class="col-month">MONTH</th>
            <th class="col-topics">TOPICS</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in subject.months"
            :key="subject.subjectID + '-m-' + row.month"
          >
            <td class="col-sno">{{ index + 1 }}</td>
            <td class="col-month">{{ row.monthName }}</td>
            <td class="col-topics">
              <ul v-if="row.topics && row.topics.length">
                <li
                  v-for="(topicItem, tIndex) in row.topics"
                  :key="tIndex"
                >
                  {{ topicLine(topicItem) }}
                </li>
              </ul>
              <span v-else>—</span>
            </td>
          </tr>
          <tr v-if="!(subject.months && subject.months.length)">
            <td colspan="3" class="text-center empty-row">
              No topics found for this subject.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import { mapActions } from "vuex";

const BREAKUP_PRINT_LOGO =
  "https://cdn.cloudious.net/file-1790760172192-231502633.jpg";

const MONTH_OPTIONS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

export default {
  name: "MonthlyBreakupPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: true,
      error: "",
      subjectBlocks: [],
      db: "",
      campusID: "",
      classIDs: "0",
      sectionID: "0",
      subjectIDs: "0",
      sessionID: "0",
      tID: "",
      title: "MONTHLY SYLLABUS COVERAGE",
      className: "",
      sectionName: "",
      subjectName: "",
      sessionName: "",
      campusName: "",
      campusTagline: "",
      teacherName: "",
      logo: BREAKUP_PRINT_LOGO,
    };
  },
  computed: {
    titleText() {
      return String(this.title || "FIRST TERM SYLLABUS").toUpperCase();
    },
    campusInitials() {
      return String(this.campusName || "S")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },
    showCampusNameText() {
      return !this.logo && !!(this.campusName || "").trim();
    },
  },
  async created() {
    this.readQuery();
    await this.loadData();
  },
  methods: {
    ...mapActions({ get: "get" }),

    readQuery() {
      const q = this.$route.query || {};
      this.db = q.db || this.$store.state.userData.db;
      this.campusID = q.campusID || this.$store.state.userData.cId;
      // support both multi (classIDs) and legacy single (classID)
      this.classIDs = q.classIDs || q.classID || "0";
      this.sectionID = q.sectionID || "0";
      this.subjectIDs = q.subjectIDs || q.subjectID || "0";
      this.sessionID = q.sessionID || "0";
      this.tID = q.tID || "";
      this.className = q.className || "";
      this.sectionName = q.sectionName || "";
      this.subjectName = q.subjectName || "";
      this.sessionName = q.sessionName || "";
      this.campusName = q.campusName || this.$store.state.branchName || "";
      this.campusTagline = q.campusTagline || "";
      this.teacherName = q.teacherName || "";
      this.logo = BREAKUP_PRINT_LOGO;
      if (q.title) this.title = q.title;
    },

    reportTitle(subject) {
      const range = this.monthRangeLabel(subject);
      const base = this.titleText;
      if (range) return base + " (" + range + ")";
      return base;
    },

    subjectClassLabel(subject) {
      const className = subject.className || this.className || "";
      const sectionName =
        subject.sectionName ||
        (this.sectionName && this.sectionName !== "All sections"
          ? this.sectionName
          : "");
      if (className && sectionName) return className + " / " + sectionName;
      return className || "—";
    },

    monthName(month) {
      return (
        (MONTH_OPTIONS.find((m) => Number(m.value) === Number(month)) || {})
          .label || String(month || "—")
      );
    },

    topicLine(topicItem) {
      if (typeof topicItem === "string") return topicItem;
      const topic = String((topicItem && topicItem.topic) || "").trim();
      const subTopic = String((topicItem && topicItem.subTopic) || "").trim();
      if (topic && subTopic) return topic + " — " + subTopic;
      return topic || subTopic || "—";
    },

    sessionYearParts() {
      const session = String(this.sessionName || "");
      const match = session.match(/(20\d{2})\s*[-–\/]\s*(20\d{2}|\d{2})/);
      if (!match) return { start: "", end: "" };
      const start = match[1];
      let end = match[2];
      if (end.length === 2) end = start.slice(0, 2) + end;
      return { start, end };
    },

    yearForMonth(monthNum) {
      const parts = this.sessionYearParts();
      const m = Number(monthNum);
      if (!parts.start) return "";
      // Session year typically Jul/Aug→Jun: months >= 7 belong to start year
      if (m >= 7) return parts.start;
      return parts.end || parts.start;
    },

    monthRangeLabel(subject) {
      const months = (subject && subject.months) || [];
      if (!months.length) return "";
      const first = months[0];
      const last = months[months.length - 1];
      const firstName = String(first.monthName || "").toUpperCase();
      const lastName = String(last.monthName || "").toUpperCase();
      const firstYear =
        first.year || this.yearForMonth(first.month) || "";
      const lastYear = last.year || this.yearForMonth(last.month) || "";

      if (Number(first.month) === Number(last.month)) {
        return firstYear ? firstName + " " + firstYear : firstName;
      }
      const left = firstYear ? firstName + " " + firstYear : firstName;
      const right = lastYear ? lastName + " " + lastYear : lastName;
      return left + " - " + right;
    },

    flattenSubjectsFromClasses(classes) {
      const list = [];
      (classes || []).forEach((cls) => {
        (cls.subjects || []).forEach((subject) => {
          list.push({
            ...subject,
            classID:
              subject.classID != null ? subject.classID : cls.classID,
            className: subject.className || cls.className || "",
            sectionID:
              subject.sectionID != null ? subject.sectionID : cls.sectionID,
            sectionName: subject.sectionName || cls.sectionName || "",
          });
        });
      });
      return list;
    },

    groupItemsByMonth(items) {
      const map = new Map();
      (items || []).forEach((item) => {
        const month = Number(item.month) || 0;
        if (!map.has(month)) {
          map.set(month, {
            month,
            monthName: item.monthName || this.monthName(month),
            topics: [],
          });
        }
        const topic = String(item.topic || "").trim();
        const subTopic = String(item.subTopic || "").trim();
        if (topic || subTopic) {
          map.get(month).topics.push({ topic, subTopic });
        }
      });
      return Array.from(map.values()).sort((a, b) => a.month - b.month);
    },

    buildSubjectBlock(entry) {
      const items = Array.isArray(entry.items) ? entry.items : [];
      const first = items[0] || {};

      // Prefer entry-level fields, then first item
      const teacherName =
        entry.teacherName ||
        entry.staffName ||
        first.teacherName ||
        first.staffName ||
        "";
      const sectionName =
        entry.sectionName || first.sectionName || this.sectionName || "";
      const sectionID =
        entry.sectionID ?? first.sectionID ?? this.sectionID ?? null;

      if (first.sessionName && !this.sessionName) {
        this.sessionName = first.sessionName;
      }
      if (entry.sessionName && !this.sessionName) {
        this.sessionName = entry.sessionName;
      }
      if (first.title && !this.title) {
        this.title = first.title;
      }
      if (entry.title) {
        this.title = entry.title;
      }

      let months = [];
      if (Array.isArray(entry.months) && entry.months.length) {
        months = entry.months
          .map((m) => ({
            month: Number(m.month) || 0,
            monthName: m.monthName || this.monthName(m.month),
            topics: Array.isArray(m.topics) ? m.topics : [],
          }))
          .sort((a, b) => a.month - b.month);
      } else {
        months = this.groupItemsByMonth(items);
      }

      return {
        subjectID: entry.subjectID,
        subjectName: entry.subjectName || entry.subject || "",
        teacherName,
        classID: entry.classID,
        className: entry.className || entry.name || "",
        sectionID,
        sectionName,
        months,
      };
    },

    normalizeSubjects(payload) {
      // Actual API: { status, data: [ { classID, className, subjectID, subjectName, items: [...] } ] }
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : null;

      if (list) {
        // optional top-level meta if backend adds it later
        if (payload?.sessionName) this.sessionName = payload.sessionName;
        if (payload?.title) this.title = payload.title;
        if (payload?.campusName) this.campusName = payload.campusName;
        this.logo = BREAKUP_PRINT_LOGO;

        return list
          .map((entry) => this.buildSubjectBlock(entry))
          .filter((s) => s.subjectName || s.months.length);
      }

      // Legacy / nested shapes
      const root = payload?.data || payload || {};
      if (root.title) this.title = root.title;
      if (root.sessionName) this.sessionName = root.sessionName;
      if (root.className) this.className = root.className;
      if (root.sectionName) this.sectionName = root.sectionName;
      if (root.campusName) this.campusName = root.campusName;
      if (root.campusTagline) this.campusTagline = root.campusTagline;
      this.logo = BREAKUP_PRINT_LOGO;

      let subjects = [];
      if (Array.isArray(root.subjects)) {
        subjects = root.subjects;
      } else if (Array.isArray(root.classes)) {
        subjects = this.flattenSubjectsFromClasses(root.classes);
      }

      return subjects
        .map((subject) => this.buildSubjectBlock(subject))
        .filter((s) => s.subjectName || s.months.length);
    },

    async loadData() {
      this.loading = true;
      this.error = "";
      try {
        let url =
          this.$store.state.domain +
          "MonthlyBreakup/GetReport?db=" +
          encodeURIComponent(this.db) +
          "&campusID=" +
          this.campusID +
          "&classIDs=" +
          encodeURIComponent(this.classIDs || "0") +
          "&sectionID=" +
          (this.sectionID || "0") +
          "&subjectIDs=" +
          encodeURIComponent(this.subjectIDs || "0") +
          "&sessionID=" +
          (this.sessionID || "0");

        if (this.tID) {
          url += "&tID=" + this.tID;
        }

        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        this.subjectBlocks = this.normalizeSubjects(res);
      } catch (err) {
        console.error(err);
        this.error = "Failed to load monthly breakup report.";
        this.subjectBlocks = [];
      } finally {
        this.loading = false;
      }
    },

    print() {
      window.print();
    },

    close() {
      window.close();
      this.$router.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.mb-print {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-sheet {
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto 1rem;
  background: #fff;
  border: 3px solid #0a2a5c;
  padding: 14mm 12mm;
  color: #000;
  box-sizing: border-box;
}

.page-break {
  page-break-before: always;
}

.letterhead {
  text-align: center;
  margin-bottom: 16px;
}

.school-logo {
  max-width: 200px;
  max-height: 110px;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: 0 auto 12px;
  display: block;

  &--fallback {
    width: 78px;
    height: 78px;
    border-radius: 50%;
    background: #0a2a5c;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 24px;
  }
}

.school-name {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #000;
  font-family: Georgia, "Times New Roman", serif;
  text-transform: uppercase;
}

.school-tagline {
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #000;
}

.report-title {
  margin: 10px 0 6px;
  font-size: 16px;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-transform: uppercase;
  color: #000;
  line-height: 1.35;
}

.session-line {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-transform: uppercase;
  color: #000;
}

.meta-table,
.breakup-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.meta-table {
  margin-bottom: 16px;

  th,
  td {
    border: 1.5px solid #000;
    padding: 9px 12px;
    font-size: 14px;
    vertical-align: middle;
    text-align: left;
  }

  th {
    width: 30%;
    background: #0a2a5c !important;
    color: #fff !important;
    font-weight: 800;
  }

  td {
    background: #fff;
    color: #0a2a5c;
    font-weight: 700;
  }
}

.breakup-table {
  th,
  td {
    border: 1.5px solid #000;
    vertical-align: top;
  }

  .breakup-head {
    background: #0a2a5c !important;
    color: #fff !important;
    text-align: center;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.06em;
    padding: 8px;
    text-transform: uppercase;
  }

  .col-head th {
    background: #fff;
    color: #000;
    text-align: center;
    font-size: 13px;
    font-weight: 800;
    padding: 7px 6px;
  }

  .col-sno {
    width: 12%;
    text-align: center;
    font-weight: 800;
    padding: 8px 6px;
    color: #000;
  }

  .col-month {
    width: 22%;
    text-align: center;
    font-weight: 800;
    padding: 8px 6px;
    color: #000;
  }

  .col-topics {
    width: 66%;
    text-align: left;
    padding: 8px 10px;
    color: #000;
    font-weight: 600;

    ul {
      margin: 0;
      padding-left: 18px;
    }

    li {
      margin-bottom: 4px;
    }
  }

  .empty-row {
    padding: 18px;
    color: #333;
    font-weight: 600;
    text-align: center;
  }
}
</style>

<style lang="scss">
@media print {
  @page {
    size: A4 portrait;
    margin: 8mm;
  }

  html,
  body {
    background: #fff !important;
    height: auto !important;
    overflow: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .no-print,
  .main-menu,
  .v-nav-menu,
  .navbar,
  .navbar-container,
  .footer,
  .sidenav-overlay,
  .drag-target,
  .customizer,
  .Vue-Toastification__container {
    display: none !important;
  }

  .mb-print {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .app-content,
  .content-wrapper,
  .content-area-wrapper,
  .content-body {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .mb-print .report-sheet {
    width: 100% !important;
    min-height: auto !important;
    margin: 0 !important;
    padding: 8mm !important;
    border: 3px solid #0a2a5c !important;
    box-shadow: none !important;
    page-break-inside: avoid;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .mb-print .report-title,
  .mb-print .session-line {
    color: #000 !important;
  }

  .mb-print .meta-table th,
  .mb-print .breakup-table .breakup-head {
    background: #0a2a5c !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .mb-print .meta-table td {
    color: #0a2a5c !important;
  }

  .mb-print .meta-table th,
  .mb-print .meta-table td,
  .mb-print .breakup-table th,
  .mb-print .breakup-table td {
    border-color: #000 !important;
  }

  .mb-print .breakup-table .col-head th,
  .mb-print .breakup-table td {
    color: #000 !important;
  }

  .mb-print .page-break {
    page-break-before: always;
  }
}
</style>
